import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, X, Volume2, VolumeX } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  alt: string;
}

const mediaItems: MediaItem[] = [
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766083822/VID-20251126-WA0007_wud1nt.mp4', type: 'video', alt: 'iPhone repair process video at iFix Apple Care Patna Bihar' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766083816/IMG_20251206_165550917_HDR_fvfdmp.jpg', type: 'image', alt: 'Professional Apple device repair workstation at iFix Apple Care service center' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766083813/VID-20251204-WA0000_gcxp3k.mp4', type: 'video', alt: 'MacBook screen replacement by certified technician in Patna' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766083784/IMG-20251207-WA0000_ezmsfm.jpg', type: 'image', alt: 'iPhone screen replacement completed at iFix Apple Care Bihar' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766083782/VID-20251218-WA0018_k1gfe6.mp4', type: 'video', alt: 'iPad repair service video - screen replacement in Patna' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766083780/IMG-20251216-WA0003_mfsxsu.jpg', type: 'image', alt: 'MacBook logic board repair at Apple service center Patna' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766083779/IMG-20251218-WA0010_dsdo1t.jpg', type: 'image', alt: 'Apple Watch battery replacement at iFix Apple Care Bihar' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766083777/VID-20251216-WA0010_xnvf5z.mp4', type: 'video', alt: 'iPhone battery replacement video - same day service Patna' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766083774/IMG-20251218-WA0015_eyin9l.jpg', type: 'image', alt: 'Expert iPhone repair technician at iFix Apple Care Patna' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766086593/VID_20251017_101719237_rmmp70.mp4', type: 'video', alt: 'iPhone display replacement video at Bihar Apple service center' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766084266/IMG_20250812_172819813_HDR_wpcot8.jpg', type: 'image', alt: 'Quality iPhone screen repair work at iFix Apple Care' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766084265/IMG_20250724_163639422_HDR_h3z1xe.jpg', type: 'image', alt: 'Genuine Apple parts used for repairs at iFix Apple Care Patna' },
];

const MediaGallery = () => {
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [isMuted, setIsMuted] = useState(true);

  const getVideoThumbnail = (url: string) => {
    // Convert video URL to thumbnail by changing the extension
    return url.replace('/video/upload/', '/video/upload/so_0/').replace('.mp4', '.jpg');
  };

  return (
    <section className="section-padding bg-secondary" id="gallery">
      <div className="container-apple">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Our Work Gallery
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            See the quality of our repairs. From{' '}
            <Link to="/iphone-service" className="text-primary hover:underline">iPhone screen replacements</Link> to complex{' '}
            <Link to="/macbook-service" className="text-primary hover:underline">MacBook fixes</Link>, every device gets our expert attention.
            View our <Link to="/blog" className="text-primary hover:underline">repair guides</Link> for more information.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {mediaItems.map((item, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer group glow-card"
              onClick={() => setSelectedMedia(item)}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {item.type === 'video' ? (
                <>
                  <img
                    src={getVideoThumbnail(item.url)}
                    alt={item.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-foreground/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-background/90 flex items-center justify-center shadow-lg">
                      <Play className="w-6 h-6 text-primary ml-1" />
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src={item.url}
                  alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-4xl p-0 bg-background/95 backdrop-blur-xl border-border/50">
          <button
            onClick={() => setSelectedMedia(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedMedia?.type === 'video' ? (
            <div className="relative">
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                muted={isMuted}
                className="w-full h-auto max-h-[80vh] rounded-lg"
              />
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="absolute bottom-6 right-6 z-10 w-10 h-10 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          ) : (
            <img
              src={selectedMedia?.url}
              alt={selectedMedia?.alt || 'Apple device repair detail at iFix Apple Care Patna'}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default MediaGallery;
